// RiskAnalytics (TASK-582) — a client's LIVE portfolio risk analytics: volatility, VaR,
// beta, concentration, Sharpe + stress-test scenarios, all computed from real positions by
// portfolio-service (/risk). Replaces the deleted risk-assessment-agent (mostly-mock FINMA
// narrative) with the real calculator. "Narrative not number" prose comes from the risk
// specialist in chat; this widget shows the grounded figures. Self-fetches by clientId.
import { useQuery } from '@tanstack/react-query';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import { WidgetContainer } from '../WidgetContainer';
import { PortfolioHoldingsSpreadsheet } from './OpenAsSpreadsheet';

const pct = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)}%`);
const num = (v: number | null | undefined) => (v == null ? '—' : v.toFixed(2));

export function RiskAnalytics({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['risk-analytics', clientId],
    queryFn: () => portfolioApiService.getRiskMetrics(clientId),
    enabled: !!clientId,
  });

  const m = data?.risk_metrics;
  const dq = m?.data_quality;
  const scenarios = Object.entries(data?.stress_tests ?? {});

  return (
    // TASK-606: canvas → workbench bridge in the toolbar slot.
    <WidgetContainer source="Risk · Portfolio analytics" toolbar={<PortfolioHoldingsSpreadsheet clientId={clientId} sourceWidget="RiskAnalytics" />}>
      {isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {isError && <div className="text-[13px] text-muted-foreground">Could not load portfolio risk.</div>}
      {data && (
        <div>
          <div className="mb-3 text-[15px] font-semibold text-foreground">Portfolio risk</div>

          {dq && dq.sufficient === false && (
            <div className="mb-3 rounded bg-amber-50 p-2 text-[12px] text-amber-800">
              Insufficient history to measure risk — {dq.returns_count} day(s) on file, need ≥{dq.min_required}.
              Metrics below are shown as “—” rather than a misleading 0.
            </div>
          )}
          {dq && dq.sufficient !== false && dq.source === 'synthetic' && (
            <div className="mb-3 rounded bg-amber-50 p-2 text-[12px] text-amber-800">
              Based on simulated history — no real performance data on file yet. Figures are illustrative.
            </div>
          )}

          <dl className="space-y-1.5 text-[13px]">
            <Row label="Volatility (annualised)" value={pct(m?.volatility)} />
            <Row label="Value-at-Risk (1d, 95%)" value={pct(m?.var_1day)} />
            <Row label="Value-at-Risk (10d, 95%)" value={pct(m?.var_10day)} />
            <Row label={dq?.benchmark_used ? `Beta (vs ${dq.benchmark_used})` : 'Beta'} value={num(m?.beta)} />
            <Row label="Concentration (HHI, 0–100)" value={num(m?.concentration_risk)} />
            <Row label="Sharpe ratio" value={num(m?.sharpe_ratio)} />
          </dl>

          {scenarios.length > 0 && (
            <>
              <div className="mt-3 mb-1.5 border-t border-border pt-2 text-[12px] font-medium text-muted-foreground">
                Stress scenarios · estimated loss
              </div>
              <dl className="space-y-1.5 text-[13px]">
                {scenarios.map(([name, loss]) => (
                  <Row key={name} label={name} value={pct(Math.abs(Number(loss)))} danger />
                ))}
              </dl>
            </>
          )}

          <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground/70">
            Computed from the client's real positions and 90-day history.
          </p>
        </div>
      )}
    </WidgetContainer>
  );
}

function Row({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`font-medium ${danger ? 'text-amber-700' : 'text-foreground'}`}>{value}</dd>
    </div>
  );
}
